/**
 * Navigation configuration — defines all possible nav items and their visibility rules.
 * The useNavItems hook consumes this to produce the final nav for the current user.
 */
import { Permission } from '../utils/permissions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NavRole = 'businessAdmin' | 'companyAdmin' | 'fieldStaff';

export type SubscriptionState = 'active' | 'free-demo' | 'paused' | 'expired';

export type NavItemState = 'active' | 'locked' | 'hidden';

export interface NavItem {
    id: string;
    text: string;
    href: string;
    icon: string;
    /** Which section this item belongs to */
    section: 'main' | 'support' | 'system';
    /** Permission required (checked via hasPermission) */
    permission?: Permission;
    /** Any one of these permissions is enough. Used when view and manage are separate. */
    permissions?: Permission[];
    /** Module enrollment key on company (e.g., 'pmModuleEnrollment') */
    moduleEnrollment?: string;
    /** Roles that can see this item (if omitted, all roles) */
    roles?: NavRole[];
    /** If true, item shows for admin but text changes for field staff */
    adminOnly?: boolean;
    /** Alternative text for field staff (e.g., "My Training" instead of "Safety & Training") */
    fieldStaffText?: string;
    /** Alternative href for field staff */
    fieldStaffHref?: string;
    /** Badge key: 'issues' | 'inbox' | 'companies' */
    badgeKey?: 'issues' | 'inbox' | 'companies';
    /** Sort order within section */
    order: number;
}

export interface NavSection {
    id: string;
    label: string;
    items: ResolvedNavItem[];
}

export interface ResolvedNavItem {
    id: string;
    text: string;
    href: string;
    icon: string;
    state: NavItemState;
    badgeKey?: 'issues' | 'inbox' | 'companies';
    isLogout?: boolean;
}

export interface NavState {
    role: NavRole;
    roleBadge: { label: string; description: string };
    subscriptionState: SubscriptionState;
    companyName: string;
    companyLogo?: string;
    userName?: string;
    sections: NavSection[];
    /** Banner to show (free demo countdown, paused account, etc.) */
    banner?: NavBanner;
}

export interface NavBanner {
    type: 'free-demo' | 'paused' | 'expired';
    message: string;
    ctaText?: string;
    ctaHref?: string;
    daysRemaining?: number;
}

// ---------------------------------------------------------------------------
// All nav items (master list)
// ---------------------------------------------------------------------------

export const ALL_NAV_ITEMS: NavItem[] = [
    // ===== BUSINESS ADMIN SECTION =====
    {
        id: 'personnel',
        text: 'Personnel',
        href: '/personnel',
        icon: 'sideNav-icons/worker-contractor-svgrepo-com.svg',
        section: 'main',
        roles: ['businessAdmin'],
        order: 0,
    },
    {
        id: 'all-companies',
        text: 'All Companies',
        href: '/site-admin',
        icon: 'sideNav-icons/construction-dump-dumper-svgrepo-com.svg',
        section: 'main',
        roles: ['businessAdmin'],
        badgeKey: 'companies',
        order: 1,
    },
    {
        id: 'affiliate-dashboard',
        text: 'Affiliate Dashboard',
        href: '/affiliate-dashboard',
        icon: 'sideNav-icons/worker-contractor-svgrepo-com.svg',
        section: 'main',
        roles: ['businessAdmin'],
        order: 2,
    },
    {
        id: 'resource-library',
        text: 'Resource Library',
        href: '/resource-library',
        icon: 'library-svgrepo-com.svg',
        section: 'main',
        roles: ['businessAdmin'],
        order: 3,
    },
    {
        id: 'admin-training',
        text: 'Safety & Training',
        href: '/admin-training',
        icon: 'sideNav-icons/calendar-add-event-svgrepo-com.svg',
        section: 'main',
        roles: ['businessAdmin'],
        order: 4,
    },

    // ===== COMPANY PRODUCTS SECTION =====
    {
        id: 'home',
        text: 'Home',
        href: '/',
        icon: 'home-line-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        order: 10,
    },
    {
        id: 'regions',
        text: 'Regions',
        href: '/regions',
        icon: 'map-location-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin'],
        permission: Permission.MANAGE_REGIONS,
        order: 11,
    },
    {
        id: 'equipment',
        text: 'Equipment',
        href: '/equipment',
        icon: 'sideNav-icons/construction-dump-dumper-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        permission: Permission.MANAGE_EQUIPMENT,
        order: 12,
    },
    {
        id: 'forms',
        text: 'Forms',
        href: '/forms',
        icon: 'sideNav-icons/checklist-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        permission: Permission.MANAGE_FORMS,
        order: 13,
    },
    {
        id: 'issues',
        text: 'Issues',
        href: '/issues',
        icon: 'sideNav-icons/warning-triangle-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        permission: Permission.VIEW_DISCREPANCIES,
        badgeKey: 'issues',
        order: 14,
    },
    {
        id: 'pm',
        text: 'Preventive Maintenance',
        href: '/pm-management',
        icon: 'sideNav-icons/pm-icon.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        moduleEnrollment: 'pmModuleEnrollment',
        permissions: [Permission.VIEW_PM, Permission.MANAGE_PM],
        order: 15,
    },
    {
        id: 'tires',
        text: 'Tire Management',
        href: '/tire-maintenance',
        icon: 'sideNav-icons/tire-maintenance-icon.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        moduleEnrollment: 'tiresModuleEnrollment',
        permission: Permission.MANAGE_TIRE_MAINTENANCE,
        order: 16,
    },
    {
        id: 'safety-training',
        text: 'Safety & Training',
        href: '/safety-training',
        icon: 'sideNav-icons/calendar-add-event-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        moduleEnrollment: 'trainingModuleEnrollment',
        permission: Permission.MANAGE_SAFETY_AND_TRAINING,
        fieldStaffText: 'My Training',
        fieldStaffHref: '/my-training',
        order: 17,
    },
    {
        id: 'cost-tracking',
        text: 'Cost Tracking',
        href: '/cost-tracking',
        icon: 'sideNav-icons/cost-module-icon.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        moduleEnrollment: 'costTrackingModuleEnrollment',
        permission: Permission.MANAGE_COST_TRACKING,
        order: 18,
    },
    {
        id: 'daily-briefing',
        text: 'Daily Briefing',
        href: '/daily-briefing',
        icon: 'sideNav-icons/daily-briefing-icon.svg',
        section: 'main',
        roles: ['companyAdmin'],
        permission: Permission.MANAGE_SAFETY_AND_TRAINING,
        order: 18.5,
    },
    {
        id: 'reports',
        text: 'Reports',
        href: '/reports',
        icon: 'sideNav-icons/documents-svgrepo.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        permission: Permission.VIEW_REPORTS,
        order: 19,
    },
    {
        id: 'resources',
        text: 'Resources',
        href: '/resources',
        icon: 'sideNav-icons/documents-document-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin', 'fieldStaff'],
        permission: Permission.VIEW_RESOURCES,
        order: 20,
    },
    {
        id: 'users',
        text: 'Users',
        href: '/users',
        icon: 'sideNav-icons/worker-contractor-svgrepo-com.svg',
        section: 'main',
        roles: ['companyAdmin'],
        permission: Permission.VIEW_USERS,
        adminOnly: true,
        fieldStaffText: 'My Profile',
        fieldStaffHref: '/my-profile',
        order: 21,
    },
    {
        id: 'user-skillsets',
        text: 'User Skillsets',
        href: '/user-skillsets',
        icon: 'sideNav-icons/skillset-icon.svg',
        section: 'main',
        roles: ['companyAdmin'],
        permission: Permission.MANAGE_SKILL_TRACKING,
        order: 22,
    },
    {
        id: 'my-profile',
        text: 'My Profile',
        href: '/my-profile',
        icon: 'sideNav-icons/worker-contractor-svgrepo-com.svg',
        section: 'main',
        roles: ['fieldStaff'],
        order: 21,
    },

    // ===== SUPPORT SECTION =====
    {
        id: 'inbox',
        text: 'Inbox',
        href: '/inbox',
        icon: 'sideNav-icons/inbox-icon.svg',
        section: 'support',
        badgeKey: 'inbox',
        order: 30,
    },
    {
        id: 'get-help',
        text: 'Get Help',
        href: '/help',
        icon: 'sideNav-icons/get-help-icon.svg',
        section: 'support',
        order: 31,
    },

    // ===== SYSTEM SECTION =====
    {
        id: 'settings',
        text: 'Settings & Billing',
        href: '/company',
        icon: 'sideNav-icons/settings-icon.svg',
        section: 'system',
        roles: ['businessAdmin', 'companyAdmin'],
        fieldStaffText: 'Settings',
        order: 40,
    },
    {
        id: 'settings-staff',
        text: 'Settings',
        href: '/company',
        icon: 'sideNav-icons/settings-icon.svg',
        section: 'system',
        roles: ['fieldStaff'],
        order: 40,
    },
    {
        id: 'logout',
        text: 'Log Out',
        href: '/logout',
        icon: 'sideNav-icons/logout-svgrepo-com.svg',
        section: 'system',
        order: 50,
    },
];

// ---------------------------------------------------------------------------
// Section labels by role
// ---------------------------------------------------------------------------

export function getSectionLabel(sectionId: string, role: NavRole, companyName?: string): string {
    switch (sectionId) {
        case 'main':
            if (role === 'businessAdmin') return 'SITE ADMIN TOOLS';
            return `${companyName?.toUpperCase() || 'COMPANY'} PRODUCTS`;
        case 'support':
            return 'SUPPORT';
        case 'system':
            return '';
        default:
            return '';
    }
}

// ---------------------------------------------------------------------------
// Role badge config
// ---------------------------------------------------------------------------

export function getRoleBadge(role: NavRole): { label: string; description: string } {
    switch (role) {
        case 'businessAdmin':
            return { label: '⚡ SUPER ADMIN', description: 'Full system access across all companies' };
        case 'companyAdmin':
            return { label: 'ADMIN ACCOUNT', description: '' };
        case 'fieldStaff':
            return { label: 'STAFF', description: '' };
    }
}
